import { Session, User } from '@supabase/supabase-js';
import { useContext, useState, useEffect, createContext, useRef } from 'react';
import { supabaseClient } from '@/utils/supabaseClient';
import SessionTimeoutHandler from './SessionTimeoutHandler';

const AuthContext = createContext<{
  session: Session | null | undefined;
  user: User | null | undefined;
  access_token: string | null | undefined;
  loading: boolean;
  signOut: () => Promise<void>;
}>({
  session: null,
  user: null,
  access_token: null,
  loading: true,
  signOut: async () => {},
});

export const AuthProvider = ({ children }: any) => {
  const [user, setUser] = useState<User>();
  const [session, setSession] = useState<Session | null>();
  const [access_token, setAccessToken] = useState<string | null>();
  const [loading, setLoading] = useState(true);
  const listenerRef = useRef<{
    subscription: { unsubscribe: () => void };
  } | null>(null);

  // Separate effect for initial session check
  useEffect(() => {
    const getInitialSession = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabaseClient.auth.getSession();
        if (error) throw error;
        if (session) {
          setSession(session);
          setUser(session.user);
          setAccessToken(session.access_token);
        }
      } catch (error) {
        console.error('Error getting initial session:', error);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();
  }, []);

  // Separate effect for auth state changes
  useEffect(() => {
    if (listenerRef.current) return; // Prevent multiple listeners

    const { data: listener } = supabaseClient.auth.onAuthStateChange(
      (event, session) => {
        // Handle session events
        if (event === 'SIGNED_OUT') {
          localStorage.removeItem('sessionExpireAt');
        }

        // Update session state
        setSession((prev) => {
          if (JSON.stringify(prev) !== JSON.stringify(session)) {
            return session;
          }
          return prev;
        });

        setUser((prev) => {
          if (JSON.stringify(prev) !== JSON.stringify(session?.user)) {
            return session?.user;
          }
          return prev;
        });

        // Update access token
        setAccessToken((prev) => {
          const newToken = session?.access_token || null;
          if (prev !== newToken) {
            return newToken;
          }
          return prev;
        });
      }
    );

    listenerRef.current = listener;

    return () => {
      if (listenerRef.current) {
        listenerRef.current.subscription.unsubscribe();
        listenerRef.current = null;
      }
    };
  }, []);
  const handleSignOut = async () => {
    try {
      // Clear session storage
      sessionStorage.clear();
      localStorage.clear();

      // Sign out from Supabase
      await supabaseClient.auth.signOut();
    } catch (error) {
      console.error('Error during sign out:', error);
    }
  };

  const value = {
    session,
    user,
    access_token,
    loading,
    signOut: handleSignOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {session && <SessionTimeoutHandler />}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
